import type {
  Message,
  TimelineActorKind,
  TimelineEntry,
  TimelineEntryDto,
  TimelineEntryNotification,
} from '@/types';

/**
 * Chat action timeline - the pure half.
 *
 * Everything here is a function of its arguments (no store, no network, no clock), so the rules that
 * decide what a staff member sees between the messages are unit-testable without rendering anything:
 * normalising the two wire shapes, de-duplicating on `entryId`, folding a consultation request into the
 * routing line that follows it, merging the lines into the message list by time, and turning an entry
 * into a sentence in the reader's language.
 *
 * The timeline is STAFF-ONLY. Nothing in this file reaches the customer: the lines are built from the
 * staff routes and the agent hub, and the chat widget never imports this module.
 */

const ACTOR_KINDS: ReadonlySet<string> = new Set(['agent', 'bot', 'system', 'customer', 'unknown']);

/** A consultation request and the routing line that follows it are one fact; see {@link collapseConsultationPairs}. */
const PAIR_WINDOW_MS = 120_000;
/** Services stamp events with their own clocks; allow the routing line to be a hair EARLIER than the request. */
const PAIR_SKEW_MS = 2_000;

/** Rows that, when clicked, take the agent to the Bot consultation tab. */
const CONSULTATION_TYPES: ReadonlySet<string> = new Set([
  'consultation_requested',
  'consultation_assigned',
  'guidance_sent',
]);

/** True when a row of this type is a pointer into the Bot consultation pane. */
export function opensConsultation(type: string): boolean {
  return CONSULTATION_TYPES.has(type);
}

// ─── Normalisation ───────────────────────────────────────────────────────────────────────────────

const ZONE_DESIGNATOR = /(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Returns the ISO string with a zone designator, or null when it is not a date at all.
 *
 * The server stamps UTC, but a JSON round-trip can lose the trailing `Z`, and `date-fns/parseISO` reads a
 * bare `2026-10-01T14:02:00` as LOCAL time - which would move a line by the browser's UTC offset.
 */
export function ensureUtc(ts: string | null | undefined): string | null {
  const raw = (ts ?? '').trim();
  if (!raw) return null;
  const withZone = ZONE_DESIGNATOR.test(raw) ? raw : `${raw}Z`;
  return Number.isNaN(Date.parse(withZone)) ? null : withZone;
}

function normaliseKind(kind: string | null | undefined): TimelineActorKind {
  const k = (kind ?? '').trim().toLowerCase();
  return ACTOR_KINDS.has(k) ? (k as TimelineActorKind) : 'unknown';
}

function normaliseParams(params: Record<string, string> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!params || typeof params !== 'object') return out;
  for (const [k, v] of Object.entries(params)) {
    if (typeof v === 'string') out[k] = v;
    else if (v !== null && v !== undefined) out[k] = String(v);
  }
  return out;
}

function normalise(
  raw: {
    entryId?: string | null;
    sessionId?: string | null;
    type?: string | null;
    occurredAtUtc?: string | null;
    actorKind?: string | null;
    actorId?: string | null;
    params?: Record<string, string> | null;
    summary?: string | null;
  },
  fallbackSessionId?: string,
): TimelineEntry | null {
  const entryId = (raw.entryId ?? '').trim();
  // Without an id there is nothing to de-duplicate on, and a row that cannot be de-duplicated would show
  // twice the moment the same entry arrives by both the reload and the push.
  if (!entryId) return null;
  const occurredAtUtc = ensureUtc(raw.occurredAtUtc);
  // A line with no placeable time would sort to an arbitrary place in the conversation.
  if (!occurredAtUtc) return null;
  const sessionId = (raw.sessionId?.trim() || fallbackSessionId || '').trim();
  if (!sessionId) return null;
  const actorId = (raw.actorId ?? '').trim();
  return {
    entryId,
    sessionId,
    type: (raw.type ?? '').trim(),
    occurredAtUtc,
    actorKind: normaliseKind(raw.actorKind),
    actorId: actorId || null,
    params: normaliseParams(raw.params),
    summary: (raw.summary ?? '').trim(),
  };
}

/** REST entry -> the shape the console holds. Null when the entry cannot be placed or de-duplicated. */
export function timelineEntryFromDto(dto: TimelineEntryDto, fallbackSessionId?: string): TimelineEntry | null {
  if (!dto) return null;
  return normalise(
    {
      entryId: dto.entryId,
      sessionId: dto.sessionId,
      type: dto.type,
      occurredAtUtc: dto.occurredAtUtc,
      actorKind: dto.actor?.kind,
      actorId: dto.actor?.id,
      params: dto.params,
      summary: dto.summary,
    },
    fallbackSessionId,
  );
}

/** `ReceiveTimelineEntry` payload -> the shape the console holds. Null when it cannot be placed or de-duplicated. */
export function timelineEntryFromNotification(n: TimelineEntryNotification): TimelineEntry | null {
  if (!n) return null;
  return normalise({
    entryId: n.entryId,
    sessionId: n.sessionId,
    type: n.type,
    occurredAtUtc: n.occurredAtUtc,
    actorKind: n.actorKind,
    actorId: n.actorId,
    params: n.params,
    summary: n.summary,
  });
}

function ms(ts: string): number {
  return Date.parse(ts);
}

/**
 * Merges `incoming` into `existing`, de-duplicating on `entryId`, oldest first.
 *
 * The same entry reaches the console twice in the ordinary case (the reload and the live push for it), so
 * this is the single place that guarantees it is held once. Where both copies exist the INCOMING one wins:
 * the reload is authoritative and the push is best effort.
 */
export function mergeTimelineEntries(existing: TimelineEntry[], incoming: TimelineEntry[]): TimelineEntry[] {
  const byId = new Map<string, TimelineEntry>();
  for (const e of existing) byId.set(e.entryId, e);
  for (const e of incoming) byId.set(e.entryId, e);
  return [...byId.values()].sort((a, b) => ms(a.occurredAtUtc) - ms(b.occurredAtUtc) || a.entryId.localeCompare(b.entryId));
}

// ─── Folding and merging ─────────────────────────────────────────────────────────────────────────

/**
 * Folds "the bot asked for help" into the routing line that follows it.
 *
 * Two entries describe one moment: `consultation_requested` (the bot asked) and `consultation_assigned`
 * (the queue sent it to Adam, or to the whole team). The operator wants to read "Bot consulted Adam", which
 * the second line says on its own - showing both would read "Bot asked for help" and "Bot consulted Adam"
 * on the same second. A request is hidden ONLY when a routing line pairs with it (within two minutes, the
 * oldest unpaired request first); a request nobody routed - routing off, or the router never answered -
 * stays visible, because then it is the only record that the bot asked at all.
 */
export function collapseConsultationPairs(entries: TimelineEntry[]): TimelineEntry[] {
  const requested = entries
    .filter((e) => e.type === 'consultation_requested')
    .sort((a, b) => ms(a.occurredAtUtc) - ms(b.occurredAtUtc));
  if (requested.length === 0) return entries;

  const folded = new Set<string>();
  const assigned = entries
    .filter((e) => e.type === 'consultation_assigned')
    .sort((a, b) => ms(a.occurredAtUtc) - ms(b.occurredAtUtc));
  for (const a of assigned) {
    const at = ms(a.occurredAtUtc);
    const match = requested.find((r) => {
      if (folded.has(r.entryId)) return false;
      const delta = at - ms(r.occurredAtUtc);
      return delta >= -PAIR_SKEW_MS && delta <= PAIR_WINDOW_MS;
    });
    if (match) folded.add(match.entryId);
  }
  return folded.size === 0 ? entries : entries.filter((e) => !folded.has(e.entryId));
}

/** Timeline entries -> the system rows the transcript renders (after folding). */
export function timelineRows(entries: TimelineEntry[]): Message[] {
  return collapseConsultationPairs(entries).map((e) => ({
    messageId: `timeline:${e.entryId}`,
    sessionId: e.sessionId,
    // A row needs a sender to satisfy the type; `kind: 'system'` is what every renderer keys on, and these
    // rows exist only at render time - they are never written to the message store.
    sender: 'AI' as const,
    senderId: 'system',
    content: '',
    contentType: 'timeline',
    timestamp: e.occurredAtUtc,
    kind: 'system' as const,
    timeline: e,
  }));
}

/**
 * Places each timeline row among the messages by time WITHOUT reordering the messages themselves.
 *
 * Sorting the combined list would also re-sort the messages, and the message list is deliberately in
 * arrival order (an optimistic local message, a live push stamped a hair before its neighbour). Instead
 * each row goes in front of the first message that is strictly later than it; a row that is later than
 * every message goes last. Equal times keep the message first.
 */
export function mergeTimelineIntoMessages(messages: Message[], rows: Message[]): Message[] {
  if (rows.length === 0) return messages;
  const sortedRows = [...rows].sort((a, b) => ms(a.timestamp) - ms(b.timestamp));
  const out: Message[] = [];
  let r = 0;
  for (const m of messages) {
    const mt = ms(m.timestamp);
    while (r < sortedRows.length && ms(sortedRows[r].timestamp) < mt) out.push(sortedRows[r++]);
    out.push(m);
  }
  while (r < sortedRows.length) out.push(sortedRows[r++]);
  return out;
}

// ─── The one merged timeline ─────────────────────────────────────────────────────────────────────

/** The segmented filter over the merged timeline. It only narrows what is SHOWN; it never changes where an answer is sent. */
export type TimelineFilter = 'all' | 'chat' | 'consult';

/** Counts shown on the filter's segments. `all` is non-system rows; timeline lines are counted in none of them. */
export interface TimelineCounts {
  all: number;
  chat: number;
  consult: number;
}

/** A consultation row: a bot note, a teammate's guidance, a delivery-failure alert. */
function isConsultRow(m: Message): boolean {
  return m.stream === 'consult';
}

/** A staff-only timeline line between the messages (`kind: 'system'`), as opposed to a message. */
function isSystemRow(m: Message): boolean {
  return m.kind === 'system';
}

/**
 * ONE list for the transcript: customer-facing messages, the consultation log and the action timeline,
 * interleaved by time.
 *
 * The consultation log is folded into the messages, then the timeline rows are folded in. Both folds go
 * through {@link mergeTimelineIntoMessages}, which sorts the ROWS it is given by time (the consultation log
 * is held in arrival order and its rows are stamped at consume time), places each in front of the first
 * message strictly later than it, and never re-sorts the messages themselves - so two chat messages that
 * arrived out of timestamp order (an optimistic local message, a live push stamped a hair early) keep their
 * arrival order.
 *
 * Tag changes (`tags_changed`) are NOT drawn in the conversation history (operator, 2026-10-12): the tags are shown on
 * the conversation itself, and a line per attach/detach buried the messages. The server still records them.
 */
export function buildMergedTimeline(
  messages: Message[],
  consultLog: Message[] | undefined,
  timelineEntries: TimelineEntry[] | undefined,
): Message[] {
  const withConsult = mergeTimelineIntoMessages(messages, consultLog ?? []);
  const shown = (timelineEntries ?? []).filter((e) => !HIDDEN_TIMELINE_TYPES.has(e.type));
  return mergeTimelineIntoMessages(withConsult, timelineRows(shown));
}

/** Timeline entry types the conversation history does not draw. */
export const HIDDEN_TIMELINE_TYPES: ReadonlySet<string> = new Set(['tags_changed']);

/**
 * Narrows the merged timeline to one segment.
 *
 * - `all`: everything.
 * - `chat`: everything that is not a consultation row. Timeline lines STAY, so "Dana took over" still reads
 *   in context between the customer's messages.
 * - `consult`: consultation rows only; timeline lines are dropped.
 */
export function filterTimeline(items: Message[], f: TimelineFilter): Message[] {
  if (f === 'all') return items;
  if (f === 'chat') return items.filter((m) => !isConsultRow(m));
  return items.filter(isConsultRow);
}

/** Per-segment counts for the filter badges. Timeline lines are not messages and are counted nowhere. */
export function timelineCounts(items: Message[]): TimelineCounts {
  let chat = 0;
  let consult = 0;
  for (const m of items) {
    if (isSystemRow(m)) continue;
    if (isConsultRow(m)) consult += 1;
    else chat += 1;
  }
  return { all: chat + consult, chat, consult };
}

// ─── Wording ─────────────────────────────────────────────────────────────────────────────────────

/** The label keys {@link describeTimelineEntry} reads; the app's catalog must define every one. */
export type TimelineLabelKey =
  | 'tl_you'
  | 'tl_you_suffix'
  | 'tl_someone'
  | 'tl_consult_requested'
  | 'tl_consulted_agent'
  | 'tl_consulted_team'
  | 'tl_guidance_sent'
  | 'tl_takeover'
  | 'tl_takeover_reason'
  | 'tl_release'
  | 'tl_dismissed'
  | 'tl_expired'
  | 'tl_closed_customer'
  | 'tl_sla'
  | 'tl_sla_n'
  | 'tl_closed_by'
  | 'tl_closed_by_customer'
  | 'tl_closed_inactivity'
  | 'tl_closed_system'
  | 'tl_closed'
  | 'tl_spam'
  | 'tl_spam_unnamed'
  | 'tl_spam_undone'
  | 'tl_spam_undone_unnamed'
  | 'tl_draft_approved'
  | 'tl_draft_edited'
  | 'tl_draft_auto_maxwait'
  | 'tl_draft_auto_idle'
  | 'tl_draft_resolved'
  | 'tl_unknown';

export interface TimelineTextContext {
  lang: 'en' | 'he';
  /** Resolves a label in the current language, interpolating `{token}` placeholders. */
  tr: (key: TimelineLabelKey, vars?: Record<string, string | number>) => string;
  /** agentId -> display name from the tenant's agent directory; undefined when unknown or deleted. */
  nameOf: (agentId: string) => string | undefined;
  /** The signed-in staff user's id, to mark "(you)". */
  myId?: string | null;
  /** The signed-in user's own display name, used only when the directory does not list them. */
  myName?: string | null;
}

export interface TimelineText {
  text: string;
  /** Tooltip. Set for an unknown type in Hebrew (the English summary lives here, not in the line) and for a clipped reason. */
  title?: string;
}

const MAX_REASON_CHARS = 160;
const CODE_LIKE = /^[a-z0-9]+(?:_[a-z0-9]+)+$/i;

/**
 * The staff-typed reason, if there is one worth printing.
 *
 * The console's own takeover button sends the code `agent_takeover`, and other callers send codes such as
 * `agent_closed`; a snake_case token is a machine code, not words a person typed, so it is not shown.
 */
function readableReason(raw: string | undefined): { shown: string; full: string } | null {
  const reason = (raw ?? '').trim();
  if (!reason || CODE_LIKE.test(reason)) return null;
  const shown = reason.length > MAX_REASON_CHARS ? `${reason.slice(0, MAX_REASON_CHARS).trimEnd()}...` : reason;
  return { shown, full: reason };
}

function capitalizeFirst(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** A person for a sentence: their name, with "(you)" when it is the signed-in user; null when unresolvable. */
function personLabel(id: string, ctx: TimelineTextContext): string | null {
  const trimmed = id.trim();
  if (!trimmed) return null;
  const isMe = !!ctx.myId && trimmed === ctx.myId;
  const name = ctx.nameOf(trimmed) ?? (isMe ? ctx.myName?.trim() || undefined : undefined);
  if (name) return isMe ? `${name} ${ctx.tr('tl_you_suffix')}` : name;
  return isMe ? ctx.tr('tl_you') : null;
}

/**
 * The person behind an entry whose actor is expected to be staff.
 *
 * Only `actorKind === 'agent'` is taken as staff. `known` is false when the id has no name in the directory
 * (a deleted teammate, a failed directory fetch) or there is no id at all - the sentence then says "a
 * teammate", never an id and never "you".
 */
function agentOf(entry: TimelineEntry, ctx: TimelineTextContext): { label: string; known: boolean } {
  const label = entry.actorKind === 'agent' && entry.actorId ? personLabel(entry.actorId, ctx) : null;
  return label ? { label, known: true } : { label: ctx.tr('tl_someone'), known: false };
}

/**
 * The sentence for a timeline entry, in the reader's language, built from type + params - never from the
 * server's English `summary`, which carries raw ids. `summary` is used only for a type this build does not
 * know, and then only as the visible line in English (in Hebrew it moves into the tooltip).
 */
export function describeTimelineEntry(entry: TimelineEntry, ctx: TimelineTextContext): TimelineText {
  const done = (text: string, title?: string): TimelineText => ({ text: capitalizeFirst(text), title });
  const p = entry.params;

  switch (entry.type) {
    case 'consultation_requested':
      return done(ctx.tr('tl_consult_requested'));

    case 'consultation_assigned': {
      const target = (p.assignedAgentId ?? '').trim();
      const label = target ? personLabel(target, ctx) : null;
      if (target) {
        return done(ctx.tr('tl_consulted_agent', { name: label ?? ctx.tr('tl_someone') }));
      }
      return done(ctx.tr('tl_consulted_team'));
    }

    case 'guidance_sent':
      return done(ctx.tr('tl_guidance_sent', { name: agentOf(entry, ctx).label }));

    case 'takeover': {
      const who = agentOf(entry, ctx).label;
      const reason = readableReason(p.reason);
      return reason
        ? done(ctx.tr('tl_takeover_reason', { name: who, reason: reason.shown }), reason.full)
        : done(ctx.tr('tl_takeover', { name: who }));
    }

    case 'release':
      return done(ctx.tr('tl_release', { name: agentOf(entry, ctx).label }));

    case 'consultation_dismissed':
      return done(ctx.tr('tl_dismissed', { name: agentOf(entry, ctx).label }));

    case 'consultation_expired':
      return done(ctx.tr('tl_expired'));

    case 'consultation_closed_customer':
      return done(ctx.tr('tl_closed_customer'));

    case 'sla_breached': {
      const n = Number.parseInt(p.elapsedMinutes ?? '', 10);
      return done(Number.isFinite(n) && n >= 0 ? ctx.tr('tl_sla_n', { n }) : ctx.tr('tl_sla'));
    }

    case 'session_closed': {
      // Every close row says who closed (operator, 2026-10-01). Since then the server records WHAT the closer is
      // from the caller's token: `agent` (named from the staff directory), `customer` (the visitor ended their own
      // chat) or `system` (the inactivity sweep). An entry recorded before that is `unknown`, and a name is then
      // printed only on evidence: the id is a member of THIS tenant's staff directory (a visitor id never is).
      const closer = entry.actorId ?? '';
      const named = closer ? ctx.nameOf(closer) : undefined;
      if (entry.actorKind === 'customer') return done(ctx.tr('tl_closed_by_customer'));
      if (entry.actorKind === 'agent' && closer) {
        return done(ctx.tr('tl_closed_by', { name: personLabel(closer, ctx) ?? ctx.tr('tl_someone') }));
      }
      if (entry.actorKind === 'unknown' && closer && (named || (ctx.myId && closer === ctx.myId))) {
        return done(ctx.tr('tl_closed_by', { name: personLabel(closer, ctx) ?? ctx.tr('tl_someone') }));
      }
      if ((p.reason ?? '').trim().toLowerCase() === 'inactivity_timeout') return done(ctx.tr('tl_closed_inactivity'));
      if (entry.actorKind === 'system') return done(ctx.tr('tl_closed_system'));
      return done(ctx.tr('tl_closed'));
    }

    case 'session_marked_spam': {
      const who = agentOf(entry, ctx);
      return done(who.known ? ctx.tr('tl_spam', { name: who.label }) : ctx.tr('tl_spam_unnamed'));
    }

    case 'session_spam_undone': {
      const who = agentOf(entry, ctx);
      return done(who.known ? ctx.tr('tl_spam_undone', { name: who.label }) : ctx.tr('tl_spam_undone_unnamed'));
    }

    case 'email_draft_resolved': {
      switch ((p.outcome ?? '').trim()) {
        case 'approved':
          return done(ctx.tr('tl_draft_approved', { name: agentOf(entry, ctx).label }));
        case 'edited':
          return done(ctx.tr('tl_draft_edited', { name: agentOf(entry, ctx).label }));
        case 'autoSentMaxWait':
          return done(ctx.tr('tl_draft_auto_maxwait'));
        case 'autoSentIdle':
          return done(ctx.tr('tl_draft_auto_idle'));
        default:
          return done(ctx.tr('tl_draft_resolved'));
      }
    }

    default: {
      // A type this build has never seen. The summary is the server's English one-liner: shown as the line in
      // English; in Hebrew the line is generic and the English text rides in the tooltip, so Hebrew mode never
      // prints raw English summary text.
      if (ctx.lang === 'en') return done(entry.summary || ctx.tr('tl_unknown'));
      return done(ctx.tr('tl_unknown'), entry.summary || undefined);
    }
  }
}
